import './styles.scss';

import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';

import { faArrowRotateRight, faChartArea } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import { GlobalContext } from '../../providers/global-provider';
import { KEYS } from '../../providers/layout-hook';
import { CustomWidget } from '../../providers/model/custom-widget';
import { WebSocketContext } from '../../providers/websocket-context/websocket-provider';
import { getStorage, setStorage } from '../../utils/storage_service';
import { BackgroundMessage } from '../background-message';

import HandleWidgetModal from './handle-widget-modal';
import { CustomTabLayoutContext } from './custom-tab-layout-context';
import RenderWidget from './render-widget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DEFAULT_CLEAR_LAYOUT = {
  [KEYS.LG]: [],
  [KEYS.MD]: [],
  [KEYS.SM]: [],
  [KEYS.XS]: [],
  [KEYS.XXS]: [],
};

interface IProps {
  tabKey: string;
}

export default function CustomTab(props: IProps) {
  const { tabKey } = props;

  const { unsubscribeByWidget: unsubscribe } = useContext(WebSocketContext);

  const { marketDataOverviewCurrentTab, currentTab } = useContext(GlobalContext);

  const [layouts, setLayouts] = useState<any>(DEFAULT_CLEAR_LAYOUT);
  const [widgets, setWidgets] = useState<CustomWidget[]>([]);
  const [maximized] = useState('');
  const [rowExpanded, setRowExpanded] = useState<Set<string>>(new Set());
  const [baselineLayouts, setBaselineLayouts] = useState<any | null>(null);

  const [openCreateWidgetModal, setOpenCreateWidgetModal] = useState(false);
  const [currentWidget, setCurrentWidget] = useState<string | undefined>();
  const [isDeleteWidget, setIsDeleteWidget] = useState(false);
  const [expandedVolSurfaceIds, setExpandedVolSurfaceIds] = useState<string[]>([]);

  const handleCloseCreateWidgetModal = useCallback(() => {
    setOpenCreateWidgetModal(false);
  }, []);

  const handleLayoutChange = (_layout: ReactGridLayout.Layout[], lays: ReactGridLayout.Layouts) => {
    setLayouts(lays);
  };

  const changeLayoutHeight = useCallback(
    (card: string, values: { size: string; value: number }[]) => {
      setLayouts((prev: ReactGridLayout.Layouts) => {
        const next: any = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = next[key].map((item: any) => {
            const valueItem = values.find((v) => v.size === key);
            if (card === item.i && valueItem) {
              return { ...item, h: valueItem.value };
            }
            return item;
          });
        });
        return next;
      });
    },
    [],
  );

  const markVolSurfaceSmileExpanded = useCallback((id: string, expanded: boolean) => {
    setExpandedVolSurfaceIds((prev) => {
      const set = new Set(prev);
      if (expanded) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  }, []);

  const maximizeCard = useCallback(
    (card: string) => {
      const next = new Set(rowExpanded);
      if (next.has(card)) next.delete(card);
      else next.add(card);

      if (rowExpanded.size === 0 && next.size > 0 && !baselineLayouts) {
        setBaselineLayouts(layouts);
      }

      if (next.size === 0) {
        if (baselineLayouts) {
          const restored: any = JSON.parse(JSON.stringify(baselineLayouts));
          Object.keys(restored).forEach((bp) => {
            restored[bp] = (restored[bp] || []).map((item: any) => ({ ...item, static: false }));
          });
          setLayouts(restored);
        }
        setBaselineLayouts(null);
        setRowExpanded(next);
        return;
      }

      const COLS_BY_KEY: Record<string, number> = { lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 };
      const source = baselineLayouts || layouts;
      const recomputed: any = JSON.parse(JSON.stringify(source));

      Object.keys(recomputed).forEach((bp) => {
        const cols = COLS_BY_KEY[bp] ?? COLS_BY_KEY.lg;
        recomputed[bp] = (recomputed[bp] || []).map((item: any) =>
          next.has(item.i) ? { ...item, x: 0, w: cols, static: true } : { ...item, static: false },
        );
      });

      setRowExpanded(next);
      setLayouts(recomputed);
    },
    [rowExpanded, baselineLayouts, layouts],
  );

  const handleOpenModal = () => {
    setIsDeleteWidget(false);
    setOpenCreateWidgetModal(true);
  };

  const handleResetLayout = () => {
    setRowExpanded(new Set());
    setBaselineLayouts(null);
    let newLayouts: any = DEFAULT_CLEAR_LAYOUT;

    widgets.forEach((widget, index) => {
      const widgetForLayout12Columns = {
        i: widget.id,
        x: 6 * (index % 2),
        y: 4 * Math.floor(index / 2),
        w: 6,
        h: 4,
        minW: 6,
        minH: 3,
      };

      const widgetForLayoutOneColumn = {
        i: widget.id,
        x: 0,
        y: 6 * index,
        w: 12,
        h: 4,
        minW: 12,
        minH: 4,
      };

      newLayouts = {
        [KEYS.LG]: [...newLayouts[KEYS.LG], widgetForLayout12Columns],
        [KEYS.MD]: [...newLayouts[KEYS.MD], widgetForLayout12Columns],
        [KEYS.SM]: [...newLayouts[KEYS.SM], widgetForLayout12Columns],
        [KEYS.XS]: [...newLayouts[KEYS.XS], widgetForLayoutOneColumn],
        [KEYS.XXS]: [...newLayouts[KEYS.XXS], widgetForLayoutOneColumn],
      };
    });

    setLayouts(newLayouts);
  };

  const handleAddWidget = (selectedWidgets: CustomWidget[]) => {
    setRowExpanded(new Set());
    setBaselineLayouts(null);
    setWidgets((prevWidgets) => {
      const selectedIds = selectedWidgets.map((w) => w.id);
      const keptWidgets = prevWidgets.filter((w) => selectedIds.includes(w.id));
      const newWidgets = selectedWidgets.filter(
        (w) => !prevWidgets.some((prev) => prev.id === w.id),
      );
      const updatedWidgets = [...keptWidgets, ...newWidgets];
      setLayouts(() => {
        const newLayouts: any = DEFAULT_CLEAR_LAYOUT;

        updatedWidgets.forEach((widget, index) => {
          const widgetForLayout12Columns = {
            i: widget.id,
            x: 6 * (index % 2),
            y: 4 * Math.floor(index / 2),
            w: 6,
            h: 4,
            minW: 6,
            minH: 3,
          };

          const widgetForLayoutOneColumn = {
            i: widget.id,
            x: 0,
            y: 6 * index,
            w: 12,
            h: 4,
            minW: 12,
            minH: 4,
          };

          newLayouts[KEYS.LG].push(widgetForLayout12Columns);
          newLayouts[KEYS.MD].push(widgetForLayout12Columns);
          newLayouts[KEYS.SM].push(widgetForLayout12Columns);
          newLayouts[KEYS.XS].push(widgetForLayoutOneColumn);
          newLayouts[KEYS.XXS].push(widgetForLayoutOneColumn);
        });

        return newLayouts;
      });

      return updatedWidgets;
    });
  };

  const handleDuplicateWidget = (widgetId: string) => {
    const randomId = Math.floor(Math.random() * 10000);

    const widgetToDuplicate = widgets.find((w) => w.key === widgetId);

    setWidgets((prevWidgets) => {
      const index = prevWidgets.findIndex((w) => w.key === widgetId);

      if (index === -1) return prevWidgets;

      const widgetToDuplicate = prevWidgets[index];
      const duplicatedWidget = {
        ...widgetToDuplicate,
        id: `${widgetToDuplicate.key}_${randomId}`,
      };

      const newWidgets = [...prevWidgets];
      newWidgets.splice(index + 1, 0, duplicatedWidget);

      return newWidgets;
    });

    setLayouts((prevLayouts: any) => {
      const newLayouts: any = { ...prevLayouts };

      Object.keys(newLayouts).forEach((key) => {
        const layoutsArray = [...newLayouts[key]];
        const layoutIndex = layoutsArray.findIndex(
          (layout: any) => layout.i === widgetToDuplicate?.id,
        );

        if (layoutIndex === -1) return;

        const widgetLayout = layoutsArray[layoutIndex];
        const duplicatedLayout = {
          ...widgetLayout,
          i: `${widgetLayout.i}_${randomId}`,
        };

        layoutsArray.splice(layoutIndex + 1, 0, duplicatedLayout);

        layoutsArray.forEach((layout: any, idx: number) => {
          layout.x = 6 * (idx % 2);
          layout.y = 4 * Math.floor(idx / 2);
        });

        newLayouts[key] = layoutsArray;
      });

      return newLayouts;
    });
  };

  const handleDeleteWidget = (widget: string) => {
    setCurrentWidget(widget);
    setIsDeleteWidget(true);
    setOpenCreateWidgetModal(true);
  };

  const handleUnsubscribeWidget = (widget: string) => {
    unsubscribe(widget);
  };

  const deleteWidget = (id: string) => {
    setCurrentWidget(undefined);
    setIsDeleteWidget(false);

    setWidgets((prev) => {
      return prev.filter((w) => w.id !== id);
    });

    handleUnsubscribeWidget(id);

    setLayouts((prev: any) => {
      const filterAndReposition = (arr: any) => {
        return arr
          .filter((w: any) => w.i !== id)
          .map((item: any, idx: number) => ({
            ...item,
            x: 6 * (idx % 2),
            y: 4 * Math.floor(idx / 2),
          }));
      };

      return {
        [KEYS.LG]: filterAndReposition(prev[KEYS.LG]),
        [KEYS.MD]: filterAndReposition(prev[KEYS.MD]),
        [KEYS.SM]: filterAndReposition(prev[KEYS.SM]),
        [KEYS.XS]: filterAndReposition(prev[KEYS.XS]),
        [KEYS.XXS]: filterAndReposition(prev[KEYS.XXS]),
      };
    });
  };

  useEffect(() => {
    const saved = getStorage(`WIDGETS_${tabKey}`);

    if (saved) {
      const persistedExpanded: string[] = saved.expandedVolSurfaceIds || [];
      setExpandedVolSurfaceIds(persistedExpanded);
      setWidgets(saved.widgets);

      const resetToggledHeights = (
        prevLayouts: ReactGridLayout.Layouts,
        expandedIds: string[],
      ): ReactGridLayout.Layouts => {
        try {
          if (!expandedIds || expandedIds.length === 0) return prevLayouts;
          const next: any = { ...prevLayouts };
          Object.keys(next).forEach((key) => {
            next[key] = (next[key] || []).map((item: any) =>
              expandedIds.includes(item.i) ? { ...item, h: 4 } : item,
            );
          });
          return next;
        } catch {
          return prevLayouts;
        }
      };

      const cleanedLayouts = resetToggledHeights(saved.layouts, persistedExpanded);
      Object.keys(cleanedLayouts).forEach((bp) => {
        cleanedLayouts[bp] = (cleanedLayouts[bp] || []).map((item: any) => ({
          ...item,
          static: false,
        }));
      });
      setLayouts(cleanedLayouts);
    }
  }, [setWidgets, setLayouts, tabKey]);

  useEffect(() => {
    setStorage(`WIDGETS_${tabKey}`, {
      layouts: layouts,
      widgets: widgets,
      expandedVolSurfaceIds,
    });
  }, [widgets, layouts, expandedVolSurfaceIds, tabKey]);

  useEffect(() => {
    if (marketDataOverviewCurrentTab !== tabKey || currentTab !== 'Market Data') {
      return;
    }

    if (!widgets || widgets.length === 0) {
      handleOpenModal();
    }
  }, [widgets, marketDataOverviewCurrentTab, tabKey, currentTab]);

  const renderBody = useMemo(() => {
    const widgetClassName = (key: string, type: string) => {
      if (!rowExpanded || rowExpanded.size === 0) return `${type}`;
      return rowExpanded.has(key) ? `row-maximized ${type}` : `${type}`;
    };

    return (
      <CustomTabLayoutContext.Provider value={{ changeLayoutHeight, markVolSurfaceSmileExpanded }}>
        <div className="custom-tab-container">
          <div className="main-content">
            {widgets && widgets.length > 0 ? (
              <ResponsiveGridLayout
                className="grid"
                layouts={layouts}
                autoSize={true}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 }}
                resizeHandles={['se']}
                draggableHandle=".drag-area"
                onLayoutChange={handleLayoutChange}
                useCSSTransforms={true}
                margin={[8, 8]}
              >
                {widgets.map((widget) => {
                  const isMaximized = rowExpanded.has(widget.id || '');

                  return (
                    <div key={widget.id} className={widgetClassName(widget.id || '', widget.key)}>
                      <RenderWidget
                        widget={{
                          ...widget,
                          isMaximized: isMaximized,
                        }}
                        handleSettingsWidget={handleDuplicateWidget}
                        handleDeleteWidget={handleDeleteWidget}
                        maximizeCard={maximizeCard}
                        maximized={maximized}
                        customWidgetKey={tabKey}
                        showFooter={true}
                        providerKey={widget.id}
                      />
                    </div>
                  );
                })}
              </ResponsiveGridLayout>
            ) : (
              <BackgroundMessage
                message={{
                  title: "You don't have any widgets added",
                  description:
                    'You can add the first one by clicking on the "Add a new widget" button.',
                }}
                classname="no-widgets-message"
              />
            )}
          </div>
          <div className="layout-buttons">
            <Button onClick={handleOpenModal} className="btn-add-widget">
              <FontAwesomeIcon icon={faChartArea} className="icon" />
              <span>Customize widgets</span>
            </Button>
            <Button onClick={handleResetLayout} className="btn-restore">
              <FontAwesomeIcon icon={faArrowRotateRight} />
              <span>Restore layout</span>
            </Button>

            <HandleWidgetModal
              open={openCreateWidgetModal}
              handleClose={handleCloseCreateWidgetModal}
              handleAddWidget={handleAddWidget}
              isDeleteWidget={isDeleteWidget}
              handleDeleteWidget={deleteWidget}
              currentWidget={currentWidget}
              currentWidgets={widgets}
            />
          </div>
        </div>
      </CustomTabLayoutContext.Provider>
    );
  }, [layouts, widgets, openCreateWidgetModal, isDeleteWidget, currentWidget, rowExpanded]);

  return <div className="dashboard-container">{renderBody}</div>;
}
